import React, { useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useDebounce } from "@uidotdev/usehooks";

import { useNavigation } from "../../../../Hooks/useNavigation";
import { useAIStream } from "../../../../Hooks/useAiStream";
import { useParams } from "../../../../Hooks/useNavigation";
import {
	getKnowledgeProject,
	uploadKnowledgeFiles,
	getKnowledgeProjectFiles,
	deleteKnowledgeProjectFile,
	updateKnowledgeProject,
	deleteKnowledgeProject,
} from "../../../../Api/services/ai";
import { Text } from "../../../../Components/Text";
import Grid from "../../../../Components/Grid";
import Button from "../../../../Components/Button";
import Input from "../../../../Components/Inputs/Text";
import { AutoGrowTextArea } from "../../../../Components/Inputs/AutoGrowTextArea";

import css from "./styles.styl";
import Spacer from "../../../../Components/Spacer";

const formatFileSize = (bytes) => {
	if (bytes === 0) return "0 B";
	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB", "TB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const baseStyle = {
	flex: 1,
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	padding: "20px",
	borderWidth: 2,
	borderRadius: 2,
	borderColor: "#eeeeee",
	borderStyle: "dashed",
	backgroundColor: "#fafafa",
	color: "#bdbdbd",
	outline: "none",
	transition: "border .24s ease-in-out",
};

const focusedStyle = {
	borderColor: "#2196f3",
};

const acceptStyle = {
	borderColor: "#00e676",
};

const rejectStyle = {
	borderColor: "#ff1744",
};

export const KnowledgeProjectView = () => {
	const { id } = useParams();

	const { data: knowledgeProject } = useQuery({
		queryKey: ["knowledge-project", id],
		queryFn: () => getKnowledgeProject(id),
		staleTime: 0,
	});

	if (!knowledgeProject) {
		return null;
	}

	return <KnowledgeProjectInner />;
};

export const KnowledgeProjectInner = () => {
	const { id } = useParams();
	const { navigate } = useNavigation();

	const { data: knowledgeProject, refetch: refetchKnowledgeProject } = useQuery({
		queryKey: ["knowledge-project", id],
		queryFn: () => getKnowledgeProject(id),
	});

	const { data: knowledgeFiles, refetch: refetchKnowledgeFiles } = useQuery({
		queryKey: ["knowledge-project-files", id],
		queryFn: () => getKnowledgeProjectFiles(id),
	});

	const { mutateAsync: deleteFile } = useMutation({
		mutationFn: (fileName) => deleteKnowledgeProjectFile(id, fileName),
	});

	const { mutate: updateProject } = useMutation({
		mutationFn: (update) => updateKnowledgeProject(id, update),
		onSuccess: () => refetchKnowledgeProject(),
	});

	const { data, makeRequest } = useAIStream({ url: `/ai/knowledge-projects/${id}/query` });

	const [query, setQuery] = useState("");
	const [prompt, setPrompt] = useState(knowledgeProject.prompt);
	const [name, setName] = useState(knowledgeProject.name);

	const debouncedName = useDebounce(name, 300);
	const debouncedPrompt = useDebounce(prompt, 300);

	const onDrop = async (acceptedFiles) => {
		await uploadKnowledgeFiles(id, acceptedFiles);
		refetchKnowledgeFiles();
	};

	const handleDeleteFile = async (fileName) => {
		await deleteFile(fileName);
		refetchKnowledgeFiles();
	};

	const handleSubmitQuery = async () => {
		makeRequest({ query });
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && e.shiftKey) {
			e.preventDefault();
			handleSubmitQuery();
		}
	};

	const handleDeleteProject = async () => {
		await deleteKnowledgeProject(id);
		navigate("/ai/rag");
	};

	useEffect(() => {
		updateProject({ name: debouncedName, prompt: debouncedPrompt });
	}, [debouncedName, debouncedPrompt]);

	const { getRootProps, getInputProps, isDragActive, isFocused, isDragAccept, isDragReject, acceptedFiles } =
		useDropzone({
			onDrop,
		});

	const style = useMemo(
		() => ({
			...baseStyle,
			...(isFocused ? focusedStyle : {}),
			...(isDragAccept ? acceptStyle : {}),
			...(isDragReject ? rejectStyle : {}),
		}),
		[isFocused, isDragAccept, isDragReject]
	);

	const files = (knowledgeFiles ?? []).map((file) => (
		<li className={css.file} key={file.Key}>
			<Grid columns={["20px", "1fr", "80px"]} gap={8}>
				<div role="button" onClick={() => handleDeleteFile(file.Key)} className={css.delete}>
					<Trash2 size={16} />
				</div>
				<div className={css.cell}>{file.Key}</div>
				<div className={`${css.cell} ${css.fileSize}`}>{formatFileSize(file.Size)}</div>
			</Grid>
		</li>
	));

	return (
		<div className={css.pageWrapper}>
			<Grid columns={["1fr", "450px"]} gap={80}>
				<div>
					<Text variant="caption" weight="bold">
						Name
					</Text>
					<Input value={name} onChange={setName} />

					<Spacer size={2} />

					<Text variant="caption" weight="bold">
						Custom Prompt
					</Text>
					<AutoGrowTextArea value={prompt} onChange={setPrompt} />

					<Spacer size={2} />

					<Grid columns={["1fr", "200px"]} gap={24}>
						<div>
							<Text variant="caption" weight="bold">
								Query
							</Text>
							<AutoGrowTextArea value={query} onChange={setQuery} onKeyDown={handleKeyDown} />
						</div>

						<div>
							<div style={{ height: 19 }} />
							<Button onClick={handleSubmitQuery}>Submit</Button>
						</div>
					</Grid>

					<Spacer size={2} />

					{data ? <div className={css.response}>{data}</div> : null}
				</div>

				<div>
					<Text variant="caption" weight="bold">
						Knowledge Base
					</Text>
					<div {...getRootProps({ style })}>
						<input {...getInputProps()} />
						{isDragActive ? (
							<p>Drop the files here ...</p>
						) : (
							<p>Drag 'n' drop some files here, or click to select files</p>
						)}
					</div>
					<Spacer size={2} />
					<div className={css.filesList}>
						<Text variant="caption" weight="bold">
							Files
						</Text>
						<ul>{files}</ul>
					</div>
					<Spacer size={2} />

					<Grid columns={["1fr", "1fr"]} gap={24}>
						<div />
						<Button color="red" onClick={handleDeleteProject}>
							Delete Project
						</Button>
					</Grid>
				</div>
			</Grid>
		</div>
	);
};
